import { fail, success } from '@/helpers/result-helper'
import toast from '@/services/toasts/index'
import { isSuccess, isHttpStatus } from '@/helpers/http-status-helpers'
import { orderBy } from 'lodash'
import { getSavedState, saveState, deleteState } from '@/helpers/cache-helpers'
import { isNonEmptyArray } from '@/helpers/array-helpers'
import ClientGroupOverviewWithFillRateViewModel from '@/models/overview/clientGroupOverviewWithFillRateViewModel'

const getDefaultState = () => {
  return {
    id: getSavedState('client.id'),
    name: getSavedState('client.name'),
    clientList: getSavedState('selectedClients') || [],
    grades: [],
    owners: [],
    classifications: [],
    loadingCount: 0,
    crudLoadingCount: 0,
    classificationLoadingCount: 0,
    detailsLoadingCount: 0,
    gradesLoadingCount: 0,
    overviewLoadingCount: 0,
    overviewDataLoadingCount: 0,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    moduleName: () => 'client',
    getSelectedClients: (state) => state.clientList,
    grades: (state) => (clientId) => {
      const grades = state.grades.find((x) => x.clientId === clientId)

      return grades ? orderBy(grades.list, ['name'], ['asc']) : []
    },
    owner: (state) => (clientId) => {
      const owner = state.owners.find((x) => x.clientId === clientId)
      return owner?.data
    },
    classifications: (state) => (clientId) => {
      const classifications = state.classifications.find(
        (x) => x.clientId === clientId
      )

      return classifications?.list || []
    },
    isLoading: (state) => state.loadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    isLoadingClassifications: (state) => state.classificationLoadingCount > 0,
    isLoadingClientDetails: (state) => state.detailsLoadingCount > 0,
    isLoadingGrades: (state) => state.gradesLoadingCount > 0,
    isLoadingOverview: (state) => state.overviewLoadingCount > 0,
    isLoadingOverviewData: (state) => state.overviewDataLoadingCount > 0,
    mapSelectedClientsToClientsInPermissions: (
      state,
      getters,
      rootState,
      rootGetters
    ) => {
      const allClientsInPerms = rootGetters['auth/getAllClients']
      const mappedClients = allClientsInPerms.filter((client) =>
        getters.getSelectedClients.includes(client.clientId)
      )

      return mappedClients
    },
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    START_LOADING_CLASSIFICATIONS(state) {
      state.classificationLoadingCount++
    },
    FINISH_LOADING_CLASSIFICATIONS(state) {
      state.classificationLoadingCount--
    },
    START_LOADING_DETAILS(state) {
      state.detailsLoadingCount++
    },
    FINISH_LOADING_DETAILS(state) {
      state.detailsLoadingCount--
    },
    START_LOADING_GRADES(state) {
      state.gradesLoadingCount++
    },
    FINISH_LOADING_GRADES(state) {
      state.gradesLoadingCount--
    },
    START_LOADING_OVERVIEW(state) {
      state.overviewLoadingCount++
    },
    FINISH_LOADING_OVERVIEW(state) {
      state.overviewLoadingCount--
    },
    START_LOADING_OVERVIEW_DATA(state) {
      state.overviewDataLoadingCount++
    },
    FINISH_LOADING_OVERVIEW_DATA(state) {
      state.overviewDataLoadingCount--
    },
    /**
     * TODO: Remove
     */
    SET_CLIENT(state, { clientId, clientName }) {
      state.id = clientId
      state.name = clientName

      saveState('client.id', clientId)
      saveState('client.name', clientName)
    },
    /**
     * @param {*} state
     * @param {number[]} clientsIds List of client ids
     */
    SET_CLIENTS(state, clientIds) {
      const sortedClientIds = clientIds.sort((a, b) => a - b) // Asc number sort

      state.clientList = sortedClientIds
      saveState('selectedClients', sortedClientIds)
    },
    UPSERT_CLIENT_GRADES(state, payload) {
      const found = state.grades.find((x) => x.clientId === payload.clientId)

      if (!found) {
        return state.grades.push({
          clientId: payload.clientId,
          list: payload.grades,
        })
      }

      found.list = payload.grades
    },
    UPSERT_ADDITIONAL_DETAILS(state, payload) {
      const found = state.owners.find((x) => x.clientId === payload.clientId)

      if (!found) {
        return state.owners.push({
          clientId: payload.clientId,
          data: payload.owner,
        })
      }

      found.data = payload.owner
    },
    UPSERT_CLIENT_CLASSIFICATIONS(state, payload) {
      const found = state.classifications.find(
        (x) => x.clientId === payload.clientId
      )

      if (!found) {
        return state.classifications.push({
          clientId: payload.clientId,
          list: payload.classifications,
        })
      }

      found.list = payload.classifications
    },
    CLEAR_STORE(state) {
      // Clear out LocalStorage
      deleteState('client.id')
      deleteState('client.name')

      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    setClient({ commit, rootGetters, dispatch }, clientId) {
      const client = rootGetters['auth/getClient'](clientId)

      if (!client) {
        toast.error(this.$i18n.t('client.clientNotFoundErrorText'))
        return fail()
      }

      dispatch('loadClientAdditionalInformation', clientId)

      // Clear store modules of client specific data before switching
      dispatch('invoices/clear', {}, { root: true })
      dispatch('bookings/clear', {}, { root: true })
      dispatch('contacts/clear', {}, { root: true })
      dispatch('timesheets/clear', {}, { root: true })

      commit('SET_CLIENT', client)
      return success()
    },
    async loadClientAdditionalInformation({ commit, getters }, clientId) {
      const cId = clientId

      if (!cId)
        return Promise.resolve(
          fail('', 'Must select a client first before loading client details')
        )

      // Check cache
      const isCached = getters.owner(cId)

      if (isCached) return Promise.resolve(success(isCached))

      commit('START_LOADING_DETAILS')

      try {
        const response = await this.$api.client.getAdditionalDetails(cId)

        if (isSuccess(response.status)) {
          commit('UPSERT_ADDITIONAL_DETAILS', {
            owner: isHttpStatus(response.status, 204) ? [] : response.data,
            clientId: cId,
          })
          return success(response.data)
        }
      } catch (ex) {
        toast.error('Cannot load client details')
        return fail()
      } finally {
        commit('FINISH_LOADING_DETAILS')
      }
    },
    async loadClientGrades({ commit, getters }, clientId) {
      const cId = clientId

      // Check cached client grades for selected client
      const isCached = getters.grades(cId)

      if (isCached && isCached.length > 0)
        return Promise.resolve(success(isCached))

      commit('START_LOADING_GRADES')

      try {
        const response = await this.$api.client.getClientGrades(cId)

        if (isSuccess(response.status)) {
          commit('UPSERT_CLIENT_GRADES', {
            grades: isHttpStatus(response.status, 204) ? [] : response.data,
            clientId: cId,
          })
          return success(response.data)
        }
      } catch (ex) {
        toast.error('Cannot load grades')
        return fail()
      } finally {
        commit('FINISH_LOADING_GRADES')
      }
    },
    async loadClientClassifications({ commit, getters }, clientId) {
      const cId = clientId

      // Check cached client classifications for selected client
      const isCached = getters.classifications(cId)

      if (isCached && isCached.length > 0)
        return Promise.resolve(success(isCached))

      commit('START_LOADING_CLASSIFICATIONS')

      try {
        const response = await this.$api.client.getClientClassifications(cId)

        if (isSuccess(response.status)) {
          commit('UPSERT_CLIENT_CLASSIFICATIONS', {
            classifications: isHttpStatus(response.status, 204)
              ? []
              : response.data,
            clientId: cId,
          })
          return success(response.data)
        }
      } catch (ex) {
        toast.error('Cannot load classifications')
        return fail()
      } finally {
        commit('FINISH_LOADING_CLASSIFICATIONS')
      }
    },
    /**
     * Validates that the user has the permission to access the currently
     * selected client. If not, this client will be removed and another
     * will be selected
     */
    async validateSelectedClient({ rootGetters, dispatch }) {
      if (!getSavedState('client.id')) return

      const clientExistsInPermissionTree = rootGetters['auth/getClient'](
        getSavedState('client.id')
      )

      // If client exists, no further action required
      if (clientExistsInPermissionTree) return

      // User doesn't have access to current client, select first available client
      const firstAvailableClient = rootGetters['auth/firstAvailableClient']
      await dispatch('setClient', firstAvailableClient.clientId)
    },
    async loadClientGroupOverviewSchoolStatus(
      { commit, rootGetters },
      { clientGroupId, filterDate }
    ) {
      commit('START_LOADING_OVERVIEW_DATA')

      try {
        const timeZone = rootGetters[
          'auth/getTimezoneFromFirstClientGroupLocation'
        ](clientGroupId)

        const response = await this.$api.clientGroups.getOverviewData(
          clientGroupId,
          filterDate,
          timeZone
        )

        if (isSuccess(response.status)) {
          return success(
            isNonEmptyArray(response.data)
              ? response.data.map(
                  (overviewVM) =>
                    new ClientGroupOverviewWithFillRateViewModel(overviewVM)
                )
              : []
          )
        }
      } catch (ex) {
        return fail(
          ex.response?.data || {
            message: this.$i18n.t(
              'clientGroupOverview.error.noMessageFromServer'
            ),
          }
        )
      } finally {
        commit('FINISH_LOADING_OVERVIEW_DATA')
      }
    },
    setClientList({ commit }, clients) {
      commit('SET_CLIENTS', clients)
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
